<script setup lang="ts">
  import { computed, shallowRef, useId } from 'vue';
  import type { EChartsOption } from 'echarts';
  import { NumberFormatType, useLocale } from '@tundr/i18n';
  import { Tooltip } from '../Tooltip';
  import { useResolvedCssVarRecord } from '../_shared/composables/useChartColors';
  import { useECharts } from '../_shared/composables/useECharts';
  import { chartStructuralColors } from '../_shared/utils/chartColorPalette';
  import { CHART_FALLBACK_COLORS } from '../_shared/utils/chartDefaults';
  import {
    centerContentClass,
    centerLabelClass,
    centerLabelWrapperClass,
    centerValueClass,
    chartClass,
    containerClass,
    labelTooltipClass,
  } from './DonutChart.css';
  import type { DonutChartProps } from './DonutChart.types';

  const props = withDefaults(defineProps<DonutChartProps>(), {
    max: 100,
    size: 380,
  });

  const { t, n } = useLocale('ds-components');

  const uid = useId();
  const resolvedTestId = computed(
    () => props.testId ?? `ds-donut-chart-${uid}`,
  );

  const containerRef = shallowRef<HTMLElement | null>(null);
  const chartRef = shallowRef<HTMLElement | null>(null);

  const resolvedStructural = useResolvedCssVarRecord(
    containerRef,
    chartStructuralColors,
  );

  const clampedValue = computed(() =>
    Math.min(Math.max(props.value, 0), props.max),
  );

  const remaining = computed(() => props.max - clampedValue.value);

  const calcPercentage = (value: number, max: number): number =>
    max > 0 ? Math.round((value / max) * 100) : 0;

  const percentage = computed(() =>
    calcPercentage(clampedValue.value, props.max),
  );

  const displayValue = computed(
    () => props.centerCustomValue?.toString() ?? `${percentage.value}%`,
  );

  const sizeStyleObj = computed(() => ({
    width: `${props.size}px`,
    height: `${props.size}px`,
  }));

  const defaultFormatter = () =>
    `${clampedValue.value} / ${props.max} (${percentage.value}%)`;

  const meterLabel = computed(
    () =>
      props.ariaLabel ||
      props.centerLabel ||
      t('donut_chart.default_aria_label'),
  );

  const meterValueText = computed(
    () =>
      props.valueText ||
      t('donut_chart.default_value_text', {
        value: n(clampedValue.value, NumberFormatType.MAYBE_DECIMAL),
        max: n(props.max, NumberFormatType.MAYBE_DECIMAL),
        percentage: percentage.value,
      }),
  );

  const hasRange = computed(() => props.max > 0);

  const option = computed<EChartsOption | null>(() => {
    const structural = resolvedStructural.value;
    if (!structural.donutTrack) return null;

    const progressColor = props.color ?? structural.donutProgress;
    const trackColor = structural.donutTrack;

    const radius = ['65%', '80%'];

    return {
      tooltip: {
        trigger: 'item',
        formatter: props.tooltipFormatter ?? defaultFormatter,
        backgroundColor:
          structural.tooltipBackground ||
          CHART_FALLBACK_COLORS.tooltipBackground,
        textStyle: {
          color: structural.tooltipText || CHART_FALLBACK_COLORS.tooltipText,
        },
        borderColor: structural.axisLine || CHART_FALLBACK_COLORS.axisLine,
        borderWidth: 1,
      },
      series: [
        {
          type: 'pie',
          radius: radius,
          silent: true,
          label: { show: false },
          labelLine: { show: false },
          data: [{ value: 1, itemStyle: { color: trackColor } }],
          animation: false,
          z: 1,
        },
        {
          type: 'pie',
          radius: radius,
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 18,
          },
          label: { show: false },
          labelLine: { show: false },
          data: [
            {
              value: clampedValue.value,
              name: 'Progress',
              itemStyle: { color: progressColor },
            },
            {
              value: remaining.value,
              name: 'Remaining',
              itemStyle: { color: 'transparent' },
              emphasis: { disabled: true },
              tooltip: { show: false },
            },
          ],
          startAngle: 90,
          z: 2,
        },
      ],
    };
  });

  useECharts(chartRef, option);
</script>

<template>
  <div
    ref="containerRef"
    :class="containerClass"
    :style="sizeStyleObj"
    :data-testid="resolvedTestId"
  >
    <div
      ref="chartRef"
      role="meter"
      :class="chartClass"
      :aria-label="meterLabel"
      aria-valuemin="0"
      :aria-valuemax="hasRange ? max : undefined"
      :aria-valuenow="clampedValue"
      :aria-valuetext="meterValueText"
      :data-testid="`${resolvedTestId}-canvas`"
    />
    <div :class="centerContentClass" :data-testid="`${resolvedTestId}-center`">
      <slot name="centerContent">
        <Tooltip
          :title="disableTooltip ? undefined : `${centerLabel} ${displayValue}`"
          :tooltip-trigger-class="labelTooltipClass"
        >
          <div :class="centerLabelWrapperClass" aria-hidden="true">
            <div v-if="centerLabel" :class="centerLabelClass">
              {{ centerLabel }}
            </div>
            <div :class="centerValueClass">{{ displayValue }}</div>
          </div>
        </Tooltip>
      </slot>
    </div>
  </div>
</template>
